/**
 * wayai eval-results — view results from a completed eval session
 *
 * Usage:
 *   wayai eval-results --session <id>           # show aggregate results
 *   wayai eval-results --session <id> --runs    # show individual run details
 *   wayai eval-results --session <id> --json    # raw JSON output
 *   wayai eval-results --eval "Test Greeting"   # latest session results for an eval
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient } from '../lib/api-client.js';
import { printResultsTable, summarizeResponse, truncate, NO_EVALUATOR_NOTES_HINT } from '../lib/eval-format.js';

export async function evalResultsCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  // Parse args
  let sessionId: string | undefined;
  let evalName: string | undefined;
  let showRuns = false;
  let jsonOutput = false;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--session') {
      if (i + 1 >= args.length) { console.error('--session requires a value'); process.exit(1); }
      sessionId = args[++i];
    } else if (arg === '--eval') {
      if (i + 1 >= args.length) { console.error('--eval requires a value'); process.exit(1); }
      evalName = args[++i];
    } else if (arg === '--runs') {
      showRuns = true;
    } else if (arg === '--json') {
      jsonOutput = true;
    }
  }

  if (!sessionId && !evalName) {
    console.error('Usage: wayai eval-results --session <id> [--runs] [--json]');
    console.error('       wayai eval-results --eval <name> [--runs] [--json]');
    process.exit(1);
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  // If --eval, find the latest completed session containing that eval
  if (evalName && !sessionId) {
    sessionId = await findLatestSessionForEval(client, hubId, evalName);
  }

  if (!sessionId) {
    // findLatestSessionForEval already printed the error
    return;
  }

  if (showRuns) {
    await showRunDetails(client, hubId, sessionId, evalName, jsonOutput);
  } else {
    await showAggregateResults(client, hubId, sessionId, jsonOutput);
  }
}

async function findLatestSessionForEval(
  client: ApiClient,
  hubId: string,
  evalName: string
): Promise<string | undefined> {
  // Fetch evals and completed sessions in parallel
  const [evalsResult, sessionsResult] = await Promise.all([
    client.getEvals(hubId),
    client.getEvalSessions(hubId, { status: 'completed' }),
  ]);

  const matchingEval = evalsResult.data.evals.find(
    (e) => e.eval_name.toLowerCase() === evalName.toLowerCase()
  );

  if (!matchingEval) {
    console.error(`Eval "${evalName}" not found in this hub.`);
    process.exit(1);
  }

  const completedSessions = sessionsResult.data.sessions;

  if (completedSessions.length === 0) {
    console.error('No completed sessions found for this hub.');
    process.exit(1);
  }

  // Sessions are ordered by created_at desc, so first match is the latest
  for (const session of completedSessions) {
    const details = await client.getEvalSessionDetails(hubId, session.eval_session_id);
    const hasEval = details.data.results.some((r) => r.eval_id === matchingEval.eval_id);
    if (hasEval) {
      return session.eval_session_id;
    }
  }

  console.error(`No completed session found containing eval "${evalName}".`);
  process.exit(1);
}

async function showAggregateResults(
  client: ApiClient,
  hubId: string,
  sessionId: string,
  jsonOutput: boolean
): Promise<void> {
  const details = await client.getEvalSessionDetails(hubId, sessionId);

  if (jsonOutput) {
    console.log(JSON.stringify(details.data, null, 2));
    return;
  }

  const session = details.data.session;
  const results = details.data.results;

  console.log(`Session: ${session.session_name} (${session.session_status})`);
  if (session.started_at) {
    console.log(`Started: ${new Date(session.started_at).toLocaleString()}`);
  }
  if (session.completed_at && session.started_at) {
    const durationMs = new Date(session.completed_at).getTime() - new Date(session.started_at).getTime();
    console.log(`Duration: ${(durationMs / 1000).toFixed(0)}s`);
  }
  console.log('');

  printResultsTable(results, 'detailed');
}

async function showRunDetails(
  client: ApiClient,
  hubId: string,
  sessionId: string,
  evalNameFilter: string | undefined,
  jsonOutput: boolean
): Promise<void> {
  // Fetch session details (for header + eval name resolution) once
  const details = await client.getEvalSessionDetails(hubId, sessionId);

  // If filtering by eval name, resolve eval_id from the already-fetched details
  let evalId: string | undefined;
  if (evalNameFilter) {
    const matchingResult = details.data.results.find(
      (r) => r.eval?.eval_name?.toLowerCase() === evalNameFilter.toLowerCase()
    );
    if (matchingResult) {
      evalId = matchingResult.eval_id;
    }
  }

  const runsResult = await client.getEvalSessionRuns(hubId, sessionId, { evalId });

  if (jsonOutput) {
    console.log(JSON.stringify(runsResult.data, null, 2));
    return;
  }

  console.log(`Session: ${details.data.session.session_name} (${details.data.session.session_status})\n`);

  if (runsResult.data.runs.length === 0) {
    console.log('No runs found.');
    return;
  }

  let anyComment = false;
  for (const run of runsResult.data.runs) {
    const evalName = run.eval?.eval_name || run.eval_id.slice(0, 8);
    const status = run.response_match === true ? 'PASS' : run.response_match === false ? 'FAIL' : 'PENDING';
    const time = run.execution_time_ms != null ? `(${(run.execution_time_ms / 1000).toFixed(1)}s)` : '';

    console.log(`${evalName} — Run #${run.run_number} [${status}] ${time}`);

    if (run.eval_response?.content) {
      console.log(`  Response: "${truncate(run.eval_response.content, 80)}"`);
    }

    // Surface the agent's actual tool calls so tool-call evals are diagnosable
    // here, without opening the conversation (gh #2597).
    if (run.eval_response?.tool_calls && run.eval_response.tool_calls.length > 0) {
      console.log('  Tool calls:');
      for (const tc of run.eval_response.tool_calls) {
        console.log(`    ${tc.function.name}(${truncate(tc.function.arguments, 80)})`);
      }
    }

    // For a failing run, show what was expected next to the actual response
    // above — the expected-vs-actual diff that tightens diagnosis without a
    // transcript dig (gh #2813). Only when an expected was captured.
    if (run.response_match === false && run.expected_response) {
      console.log(`  Expected: ${summarizeResponse(run.expected_response)}`);
    }

    if (run.response_comment) {
      anyComment = true;
      console.log(`  Comment: ${run.response_comment}`);
    }

    if (run.scores && Object.keys(run.scores).length > 0) {
      const scoresStr = Object.entries(run.scores)
        .map(([key, val]) => `${key}: ${Math.round(val * 100)}%`)
        .join(', ');
      console.log(`  Scores: ${scoresStr}`);
    }

    // Surface the per-turn message ids so the LLM observability for either turn
    // is one command away — no need to list the conversation first:
    //   wayai conversations <conversation_id> observability --message-id <id>
    const obsParts: string[] = [];
    if (run.response_message_id) obsParts.push(`evaluated=${run.response_message_id}`);
    if (run.evaluator_message_id) obsParts.push(`evaluator=${run.evaluator_message_id}`);
    if (obsParts.length > 0 && run.conversation_id) {
      console.log(`  Observability: conversation=${run.conversation_id} ${obsParts.join(' ')}`);
    }

    console.log('');
  }

  // If the evaluator emitted no reasoning across any run, the usual cause is a
  // config gap (no `notes` evaluation variable), not a missing feature — point there.
  if (!anyComment) {
    console.log(NO_EVALUATOR_NOTES_HINT);
  }
}
